/**
 * Schema.org JSON-LD template factories for marketing pages.
 *
 * Per Phase E (MSEO-001) of the marketing-website Gen 0 remediation
 * (see `GUIDES/packages/marketing-shell/README.md`), all JSON-LD blobs
 * emitted by Colony marketing-websites flow through these pure factories.
 * Hand-written JSON-LD outside `lib/esm/seo/` is forbidden by ESLint
 * (`web/no-local-json-ld`, registered in Phase Q).
 *
 * Eight templates ship in v1:
 *   - organization        → Organization
 *   - softwareApplication → SoftwareApplication (+ optional offers/rating)
 *   - faqPage             → FAQPage
 *   - breadcrumbList      → BreadcrumbList
 *   - webPage             → WebPage
 *   - product             → Product (per pricing tier)
 *   - article             → Article (per blog post)
 *   - localBusiness       → LocalBusiness (per physical location)
 *
 * Each factory is a pure function: input → JSON-LD object. The caller is
 * responsible for serializing via `JSON.stringify(...)` and emitting as
 * `<script type="application/ld+json">…</script>` (typically via the
 * <MarketingSeo> component).
 *
 * @module @puffin-core/marketing-shell/seo/schemaTemplates
 */

import { resolveBrandTagline, DEFAULT_TAGLINE_LANGUAGE } from '@puffin-core/brand-assets';
// DTOWNRETAIL-SITE-001 U3: the LocalBusiness shape is declared ONCE in @puffin-core/seo; this template keeps
// its brand assertions and delegates the node.
// LIFE-SHORTLISTED-001 U3: so is the Organization node — `organization` was a fourth hand-written builder of it.
import {
  buildOrganizationJsonLd,
  localBusinessNode,
  postalAddressNode,
  geoCoordinatesNode,
} from '@puffin-core/seo/schema-org';

const SCHEMA_CONTEXT = 'https://schema.org';

/**
 * @typedef {Object} BrandShape  Subset of @puffin-core/brand-assets `getBrand(appKey)` output.
 * @property {string} appName
 * @property {string} tenantSubdomain
 * @property {string} legalEntity
 * @property {Record<string,string>} taglines  Per-LANGUAGE tagline map.
 * @property {Object} assets
 * @property {string} assets.ogCard
 * @property {string} assets.glyph
 * @property {string} assets.glyph512
 */

/**
 * @typedef {Object} OrganizationInput
 * @property {string} appKey
 * @property {BrandShape} brand
 * @property {string} [baseUrl]        Canonical https://www.<…>.com (defaults to brand.tenantSubdomain).
 * @property {readonly string[]} [sameAs]  Social/profile URLs (Twitter, LinkedIn, etc.).
 * @property {string} [description]
 */

/**
 * Build the Organization JSON-LD blob for a marketing-website root page.
 *
 * @param {OrganizationInput} input
 * @returns {Object}
 */
export function organization({ appKey, brand, baseUrl, sameAs, description }) {
  requireBrand('organization', brand);
  requireAppKey('organization', appKey);
  const url = baseUrl || `https://www.${brand.tenantSubdomain}`;
  const out = buildOrganizationJsonLd({
    name: brand.appName,
    legalName: brand.legalEntity,
    url,
    logo: absoluteAssetUrl(url, brand.assets.glyph512 || brand.assets.glyph),
    sameAs,
  });
  if (description) out.description = description;
  return out;
}

/**
 * @typedef {Object} SoftwareApplicationInput
 * @property {string} appKey
 * @property {BrandShape} brand
 * @property {string} [baseUrl]
 * @property {string} [applicationCategory='BusinessApplication']
 * @property {string} [operatingSystem='Web']
 * @property {readonly Object[]} [offers]
 * @property {Object} [aggregateRating]
 * @property {string} [description]
 * @property {string} [language]  Language for the brand-tagline fallback.
 */

/**
 * Build the SoftwareApplication JSON-LD blob. Defaults are tuned for the
 * Colony marketing-website use case (a SaaS marketed online).
 *
 * AAS-I18N-002 Q-F4: when no `description` is supplied the blob falls back
 * to the brand tagline, which is now per-language. This is a plain module —
 * it cannot hold a hook — so the language arrives as an explicit PARAMETER,
 * defaulting to English. A caller emitting a localized page passes its own
 * language; a caller pinning `inLanguage: 'en'` (as purchase-web's landing
 * page does today) correctly leaves it alone.
 *
 * @param {SoftwareApplicationInput} input
 * @returns {Object}
 */
export function softwareApplication({
  appKey,
  brand,
  baseUrl,
  applicationCategory = 'BusinessApplication',
  operatingSystem = 'Web',
  offers,
  aggregateRating,
  description,
  language = DEFAULT_TAGLINE_LANGUAGE,
}) {
  requireBrand('softwareApplication', brand);
  requireAppKey('softwareApplication', appKey);
  const url = baseUrl || `https://www.${brand.tenantSubdomain}`;
  const out = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'SoftwareApplication',
    name: brand.appName,
    url,
    applicationCategory,
    operatingSystem,
    image: absoluteAssetUrl(url, brand.assets.ogCard),
  };
  const taglineFallback = brand.taglines ? resolveBrandTagline(brand, language) : '';
  if (description || taglineFallback) {
    out.description = description || taglineFallback;
  }
  if (Array.isArray(offers) && offers.length > 0) {
    out.offers = offers.map((o) => ({ '@type': 'Offer', ...o }));
  }
  if (aggregateRating && typeof aggregateRating === 'object') {
    out.aggregateRating = { '@type': 'AggregateRating', ...aggregateRating };
  }
  return out;
}

/**
 * @typedef {Object} FaqItem
 * @property {string} question
 * @property {string} answer
 */

/**
 * Build the FAQPage JSON-LD blob. The `items` array MUST contain at
 * least one entry — an empty FAQPage emits a structurally-invalid
 * @graph per Google's rich-results requirements.
 *
 * @param {{items: readonly FaqItem[]}} input
 * @returns {Object}
 */
export function faqPage({ items }) {
  if (!Array.isArray(items) || items.length === 0) {
    throw new TypeError('schemaTemplates.faqPage: `items` must be a non-empty array');
  }
  for (const item of items) {
    if (!item || typeof item.question !== 'string' || typeof item.answer !== 'string') {
      throw new TypeError(
        'schemaTemplates.faqPage: every item requires string `question` + `answer`',
      );
    }
  }
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/**
 * @typedef {Object} BreadcrumbCrumb
 * @property {string} name
 * @property {string} url
 */

/**
 * Build the BreadcrumbList JSON-LD blob. The `crumbs` array MUST contain
 * at least one entry. Position is auto-numbered from 1.
 *
 * @param {{crumbs: readonly BreadcrumbCrumb[]}} input
 * @returns {Object}
 */
export function breadcrumbList({ crumbs }) {
  if (!Array.isArray(crumbs) || crumbs.length === 0) {
    throw new TypeError('schemaTemplates.breadcrumbList: `crumbs` must be a non-empty array');
  }
  for (const c of crumbs) {
    if (!c || typeof c.name !== 'string' || typeof c.url !== 'string') {
      throw new TypeError(
        'schemaTemplates.breadcrumbList: every crumb requires string `name` + `url`',
      );
    }
  }
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: c.name,
      item: c.url,
    })),
  };
}

/**
 * @typedef {Object} WebPageInput
 * @property {string} appKey
 * @property {BrandShape} brand
 * @property {string} url
 * @property {string} title
 * @property {string} [description]
 */

/**
 * Build the WebPage JSON-LD blob for a single marketing page. It names no language: the page's
 * language is the one it SHOWS, which `<MarketingSeo>` stamps from the language in force when it
 * emits the node (LIFE-SHORTLISTED-001 F-Q7-2) — every page used to pass a literal `'en'` here.
 *
 * @param {WebPageInput} input
 * @returns {Object}
 */
export function webPage({ appKey, brand, url, title, description }) {
  requireBrand('webPage', brand);
  requireAppKey('webPage', appKey);
  if (typeof url !== 'string' || url.length === 0) {
    throw new TypeError('schemaTemplates.webPage: `url` (string) is required');
  }
  if (typeof title !== 'string' || title.length === 0) {
    throw new TypeError('schemaTemplates.webPage: `title` (string) is required');
  }
  const out = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'WebPage',
    name: title,
    url,
    isPartOf: {
      '@type': 'WebSite',
      name: brand.appName,
      url: `https://www.${brand.tenantSubdomain}`,
    },
  };
  if (description) out.description = description;
  return out;
}

/**
 * @typedef {Object} TierInput
 * @property {string} name
 * @property {string} [description]
 * @property {string} amount     Numeric string (e.g. '49').
 * @property {string} [currency='USD']
 * @property {string} [period]   e.g. '/mo'.
 * @property {string} [url]      Tier-specific landing URL.
 */

/**
 * Build a Product JSON-LD blob for a pricing tier.
 *
 * @param {{appKey, brand, tier: TierInput}} input
 * @returns {Object}
 */
export function product({ appKey, brand, tier }) {
  requireBrand('product', brand);
  requireAppKey('product', appKey);
  if (!tier || typeof tier !== 'object') {
    throw new TypeError('schemaTemplates.product: `tier` (object) is required');
  }
  if (typeof tier.name !== 'string') {
    throw new TypeError('schemaTemplates.product: tier.name (string) is required');
  }
  if (typeof tier.amount !== 'string') {
    throw new TypeError('schemaTemplates.product: tier.amount (string) is required');
  }
  const out = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Product',
    name: `${brand.appName} — ${tier.name}`,
    brand: { '@type': 'Brand', name: brand.appName },
    offers: {
      '@type': 'Offer',
      price: tier.amount,
      priceCurrency: tier.currency || 'USD',
      availability: 'https://schema.org/InStock',
    },
  };
  if (tier.description) out.description = tier.description;
  if (tier.url) out.url = tier.url;
  return out;
}

/**
 * @typedef {Object} ArticleInput
 * @property {string} headline
 * @property {string} [description]
 * @property {string} url
 * @property {string} datePublished     ISO-8601 (e.g. '2026-05-14').
 * @property {string} [dateModified]
 * @property {string} [authorName]
 * @property {string} [imageUrl]
 */

/**
 * Build an Article JSON-LD blob for a blog post.
 *
 * @param {{appKey, brand, article: ArticleInput}} input
 * @returns {Object}
 */
export function article({ appKey, brand, article: input }) {
  requireBrand('article', brand);
  requireAppKey('article', appKey);
  if (!input || typeof input !== 'object') {
    throw new TypeError('schemaTemplates.article: `article` (object) is required');
  }
  for (const f of ['headline', 'url', 'datePublished']) {
    if (typeof input[f] !== 'string') {
      throw new TypeError(`schemaTemplates.article: article.${f} (string) is required`);
    }
  }
  const baseOrigin = `https://www.${brand.tenantSubdomain}`;
  const out = {
    '@context': SCHEMA_CONTEXT,
    '@type': 'Article',
    headline: input.headline,
    url: input.url,
    datePublished: input.datePublished,
    publisher: {
      '@type': 'Organization',
      name: brand.appName,
      logo: {
        '@type': 'ImageObject',
        url: absoluteAssetUrl(baseOrigin, brand.assets.glyph512 || brand.assets.glyph),
      },
    },
  };
  if (input.dateModified) out.dateModified = input.dateModified;
  if (input.description) out.description = input.description;
  if (input.authorName) {
    out.author = { '@type': 'Person', name: input.authorName };
  }
  if (input.imageUrl) {
    out.image = input.imageUrl.startsWith('http')
      ? input.imageUrl
      : absoluteAssetUrl(baseOrigin, input.imageUrl);
  }
  return out;
}

/**
 * @typedef {Object} LocalBusinessLocation
 * @property {string} name
 * @property {string} streetAddress
 * @property {string} addressLocality
 * @property {string} addressRegion
 * @property {string} postalCode
 * @property {string} [addressCountry='US']
 * @property {string} [telephone]
 * @property {string} [url]
 * @property {number} [latitude]
 * @property {number} [longitude]
 * @property {readonly Object[]} [openingHoursSpecification]
 */

/**
 * Build a LocalBusiness JSON-LD blob for a physical location.
 *
 * @param {{appKey, brand, location: LocalBusinessLocation}} input
 * @returns {Object}
 */
export function localBusiness({ appKey, brand, location }) {
  requireBrand('localBusiness', brand);
  requireAppKey('localBusiness', appKey);
  if (!location || typeof location !== 'object') {
    throw new TypeError('schemaTemplates.localBusiness: `location` (object) is required');
  }
  for (const f of ['name', 'streetAddress', 'addressLocality', 'addressRegion', 'postalCode']) {
    if (typeof location[f] !== 'string') {
      throw new TypeError(`schemaTemplates.localBusiness: location.${f} (string) is required`);
    }
  }
  const openingHours = Array.isArray(location.openingHoursSpecification)
    ? location.openingHoursSpecification.map((spec) => ({
        '@type': 'OpeningHoursSpecification',
        ...spec,
      }))
    : [];
  return {
    '@context': SCHEMA_CONTEXT,
    ...localBusinessNode({
      name: location.name,
      url: location.url,
      telephone: location.telephone,
      address: postalAddressNode({
        streetAddress: location.streetAddress,
        addressLocality: location.addressLocality,
        addressRegion: location.addressRegion,
        postalCode: location.postalCode,
        addressCountry: location.addressCountry || 'US',
      }),
      geo: geoCoordinatesNode(location),
      openingHours,
      parentOrganization: { '@type': 'Organization', name: brand.appName },
    }),
  };
}

// ────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────

function requireBrand(name, brand) {
  if (!brand || typeof brand !== 'object') {
    throw new TypeError(`schemaTemplates.${name}: \`brand\` (object) is required`);
  }
  for (const f of ['appName', 'tenantSubdomain']) {
    if (typeof brand[f] !== 'string') {
      throw new TypeError(`schemaTemplates.${name}: brand.${f} (string) is required`);
    }
  }
}

function requireAppKey(name, appKey) {
  if (typeof appKey !== 'string' || appKey.length === 0) {
    throw new TypeError(`schemaTemplates.${name}: \`appKey\` (string) is required`);
  }
}

function absoluteAssetUrl(baseUrl, assetPath) {
  if (typeof assetPath !== 'string' || assetPath.length === 0) return undefined;
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) return assetPath;
  // Normalise: baseUrl never ends with '/', assetPath always starts with '/'.
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const path = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
  return `${base}${path}`;
}

/**
 * The closed-enum dispatch table. Phase D's `<MarketingSeo>` and Phase Q's
 * cross-app contract test consume this to detect adding a new schema
 * template without updating the surface.
 */
export const SCHEMA_TEMPLATES = Object.freeze({
  organization,
  softwareApplication,
  faqPage,
  breadcrumbList,
  webPage,
  product,
  article,
  localBusiness,
});
